import { useCallback, useState } from 'react'
import axios from '@fs/zion-axios'
import { useTranslation } from 'react-i18next'
import api from '../api/chat'
import orchestrationApi from '../api/orchestration'
import { useChatState } from '../state/ChatState'
import { useBookmarks } from '../components/chat/BookmarksProvider'
import useRequestState from './useRequestState'
import { Chat } from '../global/chatSettingsConfig'

/*
  This will create a public chat room after checking if 
  the name is already taken.
  It will also bookmark this chatroom for easy access.
*/
export default function useCreateGroupChat() {
  const { connection, user } = useChatState()
  const fromJid = user.authzid
  const [createGroupChat, state] = useRequestState(api.groups.createGroupChat)
  const [lookupError, setLookupError] = useState(null)
  const { refetchBookmarks } = useBookmarks()
  const [t] = useTranslation()

  const createGroupFn = useCallback(
    async ({ description, language, friendlyName, groupType = 'GENERAL', parentJid }) => {
      setLookupError(null)

      let newGroupDetails

      try {
        newGroupDetails = await axios.post('/service/rootstech/rtc2021/orchestration/chat/groups', {
          title: friendlyName.trim(),
          description: description?.trim(),
          groupType,
          parentJid,
        })
      } catch (err) {
        if (err.response.status === 409) {
          setLookupError(
            t('chat.duplicate-chat-room', 'This name has already been taken. Please choose a different name.')
          )
          return [null]
        }
        setLookupError(t('chat.error-create-chat-room', 'Something went wrong, please try again later.'))
      }

      let groupJid

      if (newGroupDetails) {
        groupJid = `${newGroupDetails.data.jid}@${Chat.Settings.MUC_DOMAIN}`

        await createGroupChat(
          {
            connection,
            fromJid,
            chatRoom: groupJid,
            description,
            language,
            friendlyName,
          },
          [connection, createGroupChat]
        )

        await orchestrationApi.bookmarks.addBookmark({ jid: groupJid, name: friendlyName })
        refetchBookmarks()
      }

      return [groupJid, newGroupDetails?.data]
    },
    [connection, createGroupChat, fromJid, refetchBookmarks, t]
  )

  return [createGroupFn, state, lookupError]
}
